import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { t } from '@aquilixshop/i18n';
import { ProductCard } from '../../sections/_shared/ProductCard.js';
import { resolveCollectionProducts } from '../../sections/_shared/product.js';
import { marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, num, str, visibilityCss } from '../_shared/blockRender.js';
import { useProductState } from '../_shared/productContext.js';
/**
 * Shrine `complementary-products` (schema name "Complementary products", preset category "Upsells, offers
 * & gifts") — config-by-config. A "Pairs well with" cross-sell: a heading + a small paged row/slider of
 * product cards. In Shrine the products come from Shopify's Search & Discovery complementary-products
 * recommendations API (`recommendations.complementary` for the bound product); we have no such API yet, so
 * the cards are drawn from {@link resolveCollectionProducts} / demoProducts — exactly like
 * sections/related-products — capped to `product_list_limit`, shown `products_per_page` at a time, paged.
 *
 * SHRINE INVENTORY (10 settings — every one implemented + wired):
 *  - `block_heading`        → `blockHeading` (text, def "Pairs well with") — the section heading.
 *  - `make_collapsible_row` → `makeCollapsibleRow` (checkbox, def false) — wrap the whole block in a native
 *    `<details>` accordion (Shrine's collapsible variant), the heading becoming the summary.
 *  - `icon`                 → `icon` (select, 44 Shrine icon names, def "price_tag") — leading glyph on the
 *    accordion summary. Shrine only renders it on the `make_collapsible_row` path (same dependency here).
 *    Rendered as small inline SVGs (NOT the Material-Symbols glyph font — that font is a separate
 *    sub-project; see DEFERRED). A curated subset has bespoke SVGs; the rest fall back to a generic tag.
 *  - `product_list_limit`   → `productListLimit` (range 1–10 step 1, def 10) — total cards pulled.
 *  - `products_per_page`    → `productsPerPage` (range 1–4 step 1, def 3) — cards visible per page/slide
 *    (also the desktop column count).
 *  - `pagination_style`     → `paginationStyle` (select dots / counter / numbers, def "counter") — the pager
 *    UI under the row.
 *  - `image_ratio`          → `imageRatio` (select portrait / square, def "square", group "Product card") —
 *    feeds the shared ProductCard.
 *  - `enable_quick_add`     → `enableQuickAdd` (checkbox, def false, group "Product card") — feeds the
 *    shared ProductCard's quick-add pill.
 *  - `margin_top`/`margin_bottom` → marginFields (0–45 step 3 px, def 15/15).
 *
 * ID MAPPING (our id ← Shrine id, stable — never renamed): blockHeading←block_heading,
 * makeCollapsibleRow←make_collapsible_row, icon←icon, productListLimit←product_list_limit,
 * productsPerPage←products_per_page, paginationStyle←pagination_style, imageRatio←image_ratio,
 * enableQuickAdd←enable_quick_add, marginTop←margin_top, marginBottom←margin_bottom.
 *
 * Non-setting Shrine schema entries (NOT counted, reproduced structurally not as controls): the
 * informational `paragraph` (content t:…complementary_products…paragraph.content — a theme-editor hint that
 * the products come from Search & Discovery), and the two decorative headers "Product card" / "Margins"
 * (our field `group`s).
 *
 * This is a product buy-box block: it reads the shared product state and renders nothing outside a product
 * container (Shrine drops it in the product information column / under the buy box).
 *
 * SSR-safe: the first page (page 0) is rendered on the server; pagination is a `useState` index attached on
 * hydration — without JS the first `products_per_page` cards still render. The collapsible variant is a
 * native `<details>` so it opens/closes without JS too.
 *
 * DEFERRED (justified):
 *  - Shopify Search & Discovery `recommendations.complementary` product SOURCE — a Shopify-platform
 *    recommendations API with no equivalent here yet; replaced by the same demo/collection resolver
 *    related-products uses (faithful render; real complementary wiring lands with the recommendations
 *    feature). The `paragraph` editor hint that documents this source is reproduced as field `info`.
 *  - The `icon` Material-Symbols glyph FONT — Shrine renders the 44 icon names via Google's Material-Symbols
 *    font; that font is a separate sub-project. We render a curated inline-SVG subset + a generic tag SVG
 *    fallback for the rest, so the leading icon is always meaningful without the glyph font.
 *  - `settings.card_style` (a THEME-GLOBAL Shrine setting selecting the card/slide chrome) — not a block
 *    setting; our ProductCard carries its own calm card chrome.
 */
/** The 44 Shrine icon names (`none` first), surfaced as the select options. */
const ICON_NAMES = [
    'none',
    'apple',
    'banana',
    'bottle',
    'box',
    'carrot',
    'chat_bubble',
    'check_mark',
    'clipboard',
    'dairy',
    'dairy_free',
    'dryer',
    'eye',
    'fire',
    'gluten_free',
    'heart',
    'iron',
    'leaf',
    'leather',
    'lightning_bolt',
    'lipstick',
    'lock',
    'map_pin',
    'nut_free',
    'pants',
    'paw_print',
    'pepper',
    'perfume',
    'plane',
    'plant',
    'price_tag',
    'question_mark',
    'recycle',
    'return',
    'ruler',
    'serving_dish',
    'shirt',
    'shoe',
    'silhouette',
    'snowflake',
    'star',
    'stopwatch',
    'truck',
    'washing',
];
const ICON_OPTIONS = ICON_NAMES.map((value) => ({
    value,
    label: value === 'none' ? 'None' : value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
}));
export const complementaryBlockSchema = {
    type: 'complementary',
    name: 'Complementary products',
    max: 1,
    fields: [
        visibilityField,
        {
            id: 'blockHeading',
            type: 'text',
            label: 'Heading',
            default: 'Se marie bien avec',
            defaultKey: 'section.pairs_well',
            maxLength: 80,
        },
        {
            id: 'makeCollapsibleRow',
            type: 'checkbox',
            label: 'Show as collapsible row',
            info: 'Wrap the cross-sell in an expand/collapse accordion (the heading becomes the toggle).',
            default: false,
        },
        {
            id: 'icon',
            type: 'select',
            label: 'Icon',
            info: 'Leading icon shown on the collapsible-row toggle. Only used when "Show as collapsible row" is on.',
            default: 'price_tag',
            options: ICON_OPTIONS,
            visibleWhen: { field: 'makeCollapsibleRow', equals: true },
        },
        {
            id: 'productListLimit',
            type: 'range',
            label: 'Products to show',
            info: 'Complementary products are recommended automatically (Search & Discovery on a live store); this caps how many are shown.',
            min: 1,
            max: 10,
            step: 1,
            default: 10,
        },
        {
            id: 'productsPerPage',
            type: 'range',
            label: 'Products per page',
            min: 1,
            max: 4,
            step: 1,
            default: 3,
        },
        {
            id: 'paginationStyle',
            type: 'select',
            label: 'Pagination style',
            default: 'counter',
            options: [
                { value: 'dots', label: 'Dots' },
                { value: 'counter', label: 'Counter' },
                { value: 'numbers', label: 'Numbers' },
            ],
        },
        // ── Product card ────────────────────────────────────────────────────────────
        {
            id: 'imageRatio',
            type: 'select',
            label: 'Image ratio',
            group: 'Product card',
            default: 'square',
            options: [
                { value: 'portrait', label: 'Portrait' },
                { value: 'square', label: 'Square' },
            ],
        },
        {
            id: 'enableQuickAdd',
            type: 'checkbox',
            label: 'Enable quick add',
            group: 'Product card',
            default: false,
        },
        // ── Margins ─────────────────────────────────────────────────────────────────
        ...marginFields(15, 15),
    ],
};
/**
 * Inline-SVG icon catalog (NOT the Material-Symbols glyph font — deferred). A curated subset of the 44
 * Shrine names gets a bespoke stroke glyph; every other name resolves to a generic price-tag so the leading
 * icon is always present + meaningful. `none` renders nothing.
 */
function leadingIcon(name) {
    const wrap = (children) => (_jsx("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", style: { display: 'block', flex: '0 0 auto' }, children: children }));
    switch (name) {
        case 'none':
            return null;
        case 'heart':
            return wrap(_jsx("path", { d: "M12 21s-7-4.6-9.3-9C1 8.5 2.8 5 6.2 5c2 0 3.2 1.2 3.8 2.2C10.6 6.2 11.8 5 13.8 5 17.2 5 19 8.5 17.3 12 15 16.4 12 21 12 21z" }));
        case 'star':
            return wrap(_jsx("path", { d: "M12 3l2.6 5.6 6 .7-4.5 4 1.2 6L12 16.9 6.7 19.3l1.2-6-4.5-4 6-.7L12 3z" }));
        case 'truck':
        case 'plane':
            return wrap(_jsxs(_Fragment, { children: [_jsx("path", { d: "M2 7h11v8H2zM13 10h4l3 3v2h-7z" }), _jsx("circle", { cx: "6.5", cy: "17.5", r: "1.5" }), _jsx("circle", { cx: "17", cy: "17.5", r: "1.5" })] }));
        case 'lock':
            return wrap(_jsxs(_Fragment, { children: [_jsx("rect", { x: "5", y: "11", width: "14", height: "9", rx: "2" }), _jsx("path", { d: "M8 11V8a4 4 0 0 1 8 0v3" })] }));
        case 'leaf':
        case 'plant':
            return wrap(_jsx("path", { d: "M11 21c-5 0-8-3-8-8 5 0 8 3 8 8zM11 21c0-7 4-11 10-11 0 7-4 11-10 11z" }));
        case 'check_mark':
            return wrap(_jsx("path", { d: "M4 12.5 9.5 18 20 6" }));
        case 'fire':
        case 'lightning_bolt':
            return wrap(_jsx("path", { d: "M13 2 4 14h6l-1 8 9-12h-6l1-8z" }));
        case 'eye':
            return wrap(_jsxs(_Fragment, { children: [_jsx("path", { d: "M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" }), _jsx("circle", { cx: "12", cy: "12", r: "3" })] }));
        case 'recycle':
        case 'return':
            return wrap(_jsx("path", { d: "M4 9a8 8 0 0 1 14-3l2 2M20 15a8 8 0 0 1-14 3l-2-2M18 4v4h-4M6 20v-4h4" }));
        case 'ruler':
            return wrap(_jsxs(_Fragment, { children: [_jsx("rect", { x: "3", y: "8", width: "18", height: "8", rx: "1", transform: "rotate(0 12 12)" }), _jsx("path", { d: "M7 8v3M11 8v4M15 8v3M19 8v4" })] }));
        case 'map_pin':
            return wrap(_jsxs(_Fragment, { children: [_jsx("path", { d: "M12 22s7-6 7-12a7 7 0 0 0-14 0c0 6 7 12 7 12z" }), _jsx("circle", { cx: "12", cy: "10", r: "2.5" })] }));
        case 'chat_bubble':
        case 'question_mark':
            return wrap(_jsx("path", { d: "M21 12a8 8 0 0 1-11.7 7L4 20l1-5.3A8 8 0 1 1 21 12z" }));
        case 'box':
        case 'price_tag':
        default:
            // Generic price-tag — the faithful Shrine default + the fallback for every uncurated name.
            return wrap(_jsxs(_Fragment, { children: [_jsx("path", { d: "M3 12.5 11.5 4H20v8.5L11.5 21 3 12.5z" }), _jsx("circle", { cx: "15.5", cy: "8.5", r: "1.4" })] }));
    }
}
/** Pager arrow chevron (prev / next). */
const ChevronIcon = ({ dir }) => (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", "aria-hidden": "true", children: _jsx("path", { d: dir === 'prev' ? 'M10 2 4 8l6 6' : 'M6 2l6 6-6 6', stroke: "currentColor", strokeWidth: "1.7", strokeLinecap: "round", strokeLinejoin: "round" }) }));
/**
 * "Pairs well with" — heading + a paged row of {@link ProductCard}s. Total cards capped at
 * `productListLimit`; `productsPerPage` shown at once (also the desktop column count); paged via the chosen
 * `paginationStyle`. Optionally wrapped in a native `<details>` accordion (`makeCollapsibleRow`) with the
 * chosen leading `icon`. Renders nothing outside a product container.
 */
const ComplementaryBlock = ({ block, context }) => {
    const ps = useProductState();
    const [page, setPage] = useState(0);
    if (!ps)
        return null;
    const bs = block.settings;
    const cls = `aqx-cmp-${block.id}`;
    const heading = str(bs.blockHeading, 'Se marie bien avec');
    const collapsible = bs.makeCollapsibleRow === true;
    const iconName = str(bs.icon, 'price_tag');
    const limit = Math.max(1, Math.min(10, num(bs.productListLimit, 10)));
    const perPage = Math.max(1, Math.min(4, num(bs.productsPerPage, 3)));
    const paginationStyle = str(bs.paginationStyle, 'counter');
    const imageRatio = str(bs.imageRatio, 'square') ?? 'square';
    const quickAdd = bs.enableQuickAdd === true;
    // SOURCE: same resolver related-products uses (real complementary recommendations deferred). Cap to limit.
    const { products } = resolveCollectionProducts(context, limit);
    const cards = products.slice(0, limit);
    if (cards.length === 0 && context.renderMode !== 'edit')
        return null;
    const pageCount = Math.max(1, Math.ceil(cards.length / perPage));
    const safePage = Math.min(page, pageCount - 1);
    const start = safePage * perPage;
    const visible = cards.slice(start, start + perPage);
    const cardOptions = { imageRatio, showRating: true, quickAdd };
    // Desktop = `perPage` columns; mobile collapses to 2 (or 1 when perPage is 1) so cards stay legible.
    const mobileCols = perPage === 1 ? 1 : 2;
    const gridDesktop = `.${cls}__grid{grid-template-columns:repeat(${perPage},minmax(0,1fr))}`;
    const css = `.${cls}__grid{display:grid;grid-template-columns:repeat(${mobileCols},minmax(0,1fr));` +
        `gap:var(--aqx-theme-space-4, 16px);list-style:none;margin:0;padding:0}` +
        // Desktop grid via `@container aqx-page` ONLY (always present via PageRenderer) — a paired
        // @media(min-width) would fire off the builder's wide editor window inside a narrow mobile preview.
        `@container aqx-page (min-width:750px){${gridDesktop}}` +
        visibilityCss(cls, bs.visibility);
    const grid = (_jsx("ul", { className: `${cls}__grid`, children: visible.map((p) => (_jsx("li", { children: _jsx(ProductCard, { product: p, context: context, options: cardOptions }) }, p.id))) }));
    const canPage = pageCount > 1;
    const goPrev = () => setPage((p) => Math.max(0, Math.min(p, pageCount - 1) - 1));
    const goNext = () => setPage((p) => Math.min(pageCount - 1, p + 1));
    // ── Pager UI per pagination_style: dots / counter ("2 / 4") / numbers (1 2 3 …) ──────────────────
    const arrowBtn = (dir, onClick, disabled) => (_jsx("button", { type: "button", onClick: onClick, disabled: disabled, "aria-label": dir === 'prev' ? t(context, 'a11y.previous') : t(context, 'a11y.next'), style: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            inlineSize: '34px',
            blockSize: '34px',
            cursor: disabled ? 'default' : 'pointer',
            color: 'var(--aqx-scheme-text)',
            background: 'transparent',
            border: '1px solid var(--aqx-scheme-border)',
            borderRadius: 'var(--aqx-theme-radius-pill, 999px)',
            opacity: disabled ? 0.4 : 1,
        }, children: _jsx(ChevronIcon, { dir: dir }) }));
    let pagerMiddle = null;
    if (paginationStyle === 'counter') {
        pagerMiddle = (_jsxs("span", { style: {
                fontSize: 'var(--aqx-theme-text-sm, 14px)',
                color: 'var(--aqx-scheme-muted)',
                minWidth: '52px',
                textAlign: 'center',
            }, children: [safePage + 1, " / ", pageCount] }));
    }
    else if (paginationStyle === 'dots') {
        pagerMiddle = (_jsx("span", { style: { display: 'inline-flex', alignItems: 'center', gap: '8px' }, children: Array.from({ length: pageCount }).map((_, i) => (_jsx("button", { type: "button", onClick: () => setPage(i), "aria-label": `Page ${i + 1}`, "aria-current": i === safePage, style: {
                    inlineSize: i === safePage ? '9px' : '7px',
                    blockSize: i === safePage ? '9px' : '7px',
                    padding: 0,
                    cursor: 'pointer',
                    borderRadius: '50%',
                    border: 'none',
                    background: i === safePage ? 'var(--aqx-scheme-text)' : 'var(--aqx-scheme-border)',
                } }, i))) }));
    }
    else {
        // numbers
        pagerMiddle = (_jsx("span", { style: { display: 'inline-flex', alignItems: 'center', gap: '4px' }, children: Array.from({ length: pageCount }).map((_, i) => (_jsx("button", { type: "button", onClick: () => setPage(i), "aria-current": i === safePage, style: {
                    minInlineSize: '30px',
                    blockSize: '30px',
                    padding: '0 8px',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: 'var(--aqx-theme-text-sm, 14px)',
                    fontWeight: i === safePage ? 700 : 500,
                    color: i === safePage ? 'var(--aqx-scheme-bg)' : 'var(--aqx-scheme-text)',
                    background: i === safePage ? 'var(--aqx-scheme-text)' : 'transparent',
                    border: '1px solid var(--aqx-scheme-border)',
                    borderRadius: 'var(--aqx-theme-radius-md, 8px)',
                }, children: i + 1 }, i))) }));
    }
    const pager = canPage ? (_jsxs("div", { style: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--aqx-theme-space-3, 12px)',
            marginTop: 'var(--aqx-theme-space-4, 16px)',
        }, children: [arrowBtn('prev', goPrev, safePage === 0), pagerMiddle, arrowBtn('next', goNext, safePage >= pageCount - 1)] })) : null;
    const headingStyle = {
        margin: 0,
        fontFamily: 'var(--aqx-theme-heading-font, var(--aqx-theme-font))',
        fontSize: 'var(--aqx-theme-text-xl, 20px)',
        fontWeight: 'var(--aqx-theme-fw-bold, 700)',
        color: 'var(--aqx-scheme-text)',
    };
    const body = (_jsxs(_Fragment, { children: [grid, pager] }));
    // Collapsible variant: a native <details> (SSR-open by default so the cross-sell is visible without JS),
    // heading + leading icon as the summary. Caret rotates on open via the scoped style.
    const collapsibleCss = collapsible
        ? `.${cls} summary::-webkit-details-marker{display:none}` +
            `.${cls}__caret{transition:transform .25s ease}` +
            `.${cls} details[open] .${cls}__caret{transform:rotate(180deg)}`
        : '';
    return (_jsxs(_Fragment, { children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css + collapsibleCss } }), _jsx("div", { className: cls, style: { fontFamily: 'var(--aqx-theme-font)', ...marginStyle(bs, 15, 15) }, children: collapsible ? (_jsxs("details", { open: true, children: [_jsxs("summary", { style: {
                                cursor: 'pointer',
                                listStyle: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--aqx-theme-space-2, 8px)',
                                paddingBlock: 'var(--aqx-theme-space-2, 8px)',
                                borderBottom: '1px solid var(--aqx-scheme-border)',
                                marginBottom: 'var(--aqx-theme-space-4, 16px)',
                                color: 'var(--aqx-scheme-text)',
                            }, children: [_jsx("span", { style: { display: 'inline-flex', color: 'var(--aqx-scheme-accent)' }, children: leadingIcon(iconName) }), _jsx("span", { style: { ...headingStyle, flex: 1 }, children: heading }), _jsx("span", { className: `${cls}__caret`, style: { display: 'inline-flex', color: 'var(--aqx-scheme-muted)' }, children: _jsx("svg", { width: "14", height: "9", viewBox: "0 0 14 9", fill: "none", "aria-hidden": "true", children: _jsx("path", { d: "M1 1.5 7 7.5l6-6", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", strokeLinejoin: "round" }) }) })] }), body] })) : (_jsxs(_Fragment, { children: [heading ? (_jsx("h2", { style: { ...headingStyle, marginBottom: 'var(--aqx-theme-space-4, 16px)' }, children: heading })) : null, body] })) })] }));
};
export const complementaryBlock = {
    type: 'complementary',
    schema: complementaryBlockSchema,
    Component: ComplementaryBlock,
    meta: { label: 'Complementary products' },
};
//# sourceMappingURL=index.js.map